import { useShapeGroups } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  ScrollArea,
} from '@ais-ui/ui-core';
import { ChevronsUpDown } from 'lucide-react';
import { PiFolderThin } from 'react-icons/pi';
import React from 'react';
import { filterShapeGroups } from './utils';
import { useLocalization } from '@ais-ui/locale-management';

export interface GroupSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  blacklist?: string[]; // New prop for IDs that cannot be selected
  'aria-invalid'?: React.ComponentProps<'button'>['aria-invalid'];
  'aria-describedby'?: string;
}

export function GroupSelect({
  value,
  onChange,
  blacklist = [],
  ...props
}: GroupSelectProps) {
  const { messages } = useLocalization();
  const { data, isInitialLoading } = useShapeGroups();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filteredData = data != null ? filterShapeGroups(data, search) : data;

  const groups = filteredData?.flatMap((group) => flattenedGroups(group));

  // Filter out blacklisted groups
  const availableGroups = groups?.filter(
    (group) => group.id && !blacklist.includes(group.id),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between pr-3 text-base shadow-xs font-normal md:text-sm aria-invalid:shadow-destructive-sm"
          disabled={isInitialLoading}
          {...props}
        >
          {value ? (
            availableGroups?.find((shape) => shape.id === value)?.name
          ) : (
            <span>{messages.shapeGroupSelectPlaceholderText}</span>
          )}
          <span className="flex-1"></span>
          <ChevronsUpDown className="ml-2 h-12 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContentNoPortal className="w-full p-0 h-[300px]">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={messages.shapeGroupSelectSearchText}
            onValueChange={setSearch}
            value={search}
          />
          <CommandEmpty>{messages.shapeGroupSelectNoResults}</CommandEmpty>
          <ScrollArea className="flex-grow p-0 overflow-y-auto border">
            {availableGroups?.map((group) => (
              <CommandItem
                key={group.id}
                value={group.id}
                className={value === group.id ? 'bg-muted' : ''}
                onSelect={(currentValue) => {
                  onChange(currentValue === value ? '' : currentValue);
                  setOpen(false);
                }}
              >
                {group.level > 0 && (
                  <span>{new Array(group.level + 1).join('\u00A0\u00A0')}</span>
                )}
                <PiFolderThin className="mr-1.5 size-4" />
                {group.name ?? group.id ?? 'Unnamed group'}
              </CommandItem>
            ))}
          </ScrollArea>
        </Command>
      </PopoverContentNoPortal>
    </Popover>
  );
}

interface GroupSelectType {
  id: string | undefined;
  name: string | undefined;
  level: number;
}

const flattenedGroups = (group: GeoJson, level = 0): GroupSelectType[] => {
  return [
    { id: group.properties.id, level, name: group.properties.name },
    ...(group.features?.flatMap((group) => flattenedGroups(group, level + 1)) ??
      []),
  ];
};
